import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Typography, TextField, Button, Box, Checkbox, FormControlLabel, IconButton, Tooltip, Snackbar, Alert } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import { connect } from 'react-redux';
import {
  sendIAMInvite,
  resetIAMFormData,
  fetchAdvertisersListForUserBasedOnEmailId,
  fetchAdvertiserMetaDetails
} from '../../redux/user/userActions';

import AdvertiserAccessManager from './AdvertiserAccessManager';
import AdminAdvertiserAccess from './AdminAdvertiserAccess';
import IAMInviteModal from './IAMInviteModal';

import { useAdvertiserAccess } from './hooks/useAdvertiserAccess';
import { useFormValidation } from './hooks/useFormValidation';
import { useUserPrefill } from './hooks/useUserPrefill';
import { useNavigate } from 'react-router-dom';

const AddInternalUser = ({
  sendIAMInvite,
  onboardedIAMStatus,
  resetIAMFormData,
  fetchAdvertisersListForUserBasedOnEmailId,
  fetchAdvertiserMetaDetails,
  iamUserList,
  showModal,
  closeModal,
  error,
  user_role,
  userEmail,
  advertiserMetaDetails = [],
  advertisersForUserTimeZone = {
    utcAdvertisers: [],
    jstAdvertisers: [],
    orgNames: []
  }
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    org: '',
    advertiserAccess: '',
    isAdmin: false,
    newAdvertiser: ''
  });

  const [showIAMModal, setShowIAMModal] = useState(false);

  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Add state for invited user's advertiser details
  const [invitedUserAdvertiserDetails, setInvitedUserAdvertiserDetails] = useState([]);
  const [loadingInvitedUserDetails, setLoadingInvitedUserDetails] = useState(false);

  const navigate = useNavigate();

  // Fetch advertiser meta details when component mounts
  useEffect(() => {
    if (user_role === 'admin') {
      fetchAdvertiserMetaDetails();
    }
  }, [user_role, fetchAdvertiserMetaDetails]);

  const {
    allUsersList,
    loadingUsers,
    usersListError,
    invitedUserExistingAccess,
    movedFromExisting,
    setMovedFromExisting,
    setInvitedUserAccess,
    retryFetch,
    fetchAllUsersList
  } = useAdvertiserAccess();

  const { errors, validateField, isFormValid } = useFormValidation(formData, user_role, iamUserList);

  const { selectedOrgs, setSelectedOrgs, prefillFormFields, clearAutoFilledFields } = useUserPrefill(
    formData,
    setFormData,
    allUsersList,
    user_role,
    setInvitedUserAccess
  );

  // Get current user's data for access control
  const getCurrentUserData = () => {
    if (!allUsersList.length || !userEmail) {
      return null;
    }

    const foundUser = allUsersList.find((user) => user.email_id === userEmail);
    return foundUser;
  };

  const currentUserData = getCurrentUserData();

  const openAdvertiserList = () => {
    navigate('/advertisers', { replace: true });
    setOpen(false);
  };

  // Track if form has been modified
  const [isFormModified, setIsFormModified] = useState(false);

  // Track removed organizations
  const [removedOrgs, setRemovedOrgs] = useState([]);

  // Ref to access current advertiser data from child component
  const adminAdvertiserAccessRef = useRef();

  // Callback to handle removed organizations
  const handleRemovedOrgsChange = (newRemovedOrgs) => {
    setRemovedOrgs(newRemovedOrgs);
  };

  // Reset removedOrgs when email changes
  useEffect(() => {
    setRemovedOrgs([]);
  }, [formData.email]);

  // Shared button styles to avoid duplication
  const buttonStyles = {
    alignItems: 'center',
    appearance: 'none',
    backgroundColor: '#fff',
    borderRadius: '24px',
    borderStyle: 'none',
    boxShadow: 'rgba(0, 0, 0, .2) 0 3px 5px -1px, rgba(0, 0, 0, .14) 0 6px 10px 0, rgba(0, 0, 0, .12) 0 1px 18px 0',
    color: '#3c4043',
    cursor: 'pointer',
    display: 'inline-flex',
    fontFamily: '"Google Sans", Roboto, Arial, sans-serif',
    fontSize: '14px',
    fontWeight: 500,
    height: '48px',
    justifyContent: 'center',
    letterSpacing: '.25px',
    lineHeight: 'normal',
    maxWidth: '100%',
    overflow: 'visible',
    padding: '2px 24px',
    position: 'relative',
    textAlign: 'center',
    textTransform: 'none',
    transition: 'box-shadow 280ms cubic-bezier(.4, 0, .2, 1), opacity 15ms linear 30ms, transform 270ms cubic-bezier(0, 0, .2, 1) 0ms',
    userSelect: 'none',
    width: 'auto',
    willChange: 'transform,opacity',
    zIndex: 0,
    '&:hover': {
      background: '#F6F9FE',
      color: '#174ea6',
      boxShadow: 'rgba(60, 64, 67, .3) 0 2px 3px 0, rgba(60, 64, 67, .15) 0 6px 10px 4px'
    },
    '&:active': {
      boxShadow: '0 4px 4px 0 rgb(60 64 67 / 30%), 0 8px 12px 6px rgb(60 64 67 / 15%)',
      outline: 'none'
    },
    '&:focus': {
      outline: 'none',
      border: '2px solid #4285f4'
    },
    '&:not(:disabled)': {
      boxShadow: 'rgba(60, 64, 67, .3) 0 1px 3px 0, rgba(60, 64, 67, .15) 0 6px 10px 4px'
    },
    '&:disabled': {
      boxShadow: 'rgba(60, 64, 67, .3) 0 1px 3px 0, rgba(60, 64, 67, .15) 0 6px 10px 4px',
      opacity: 0.6,
      cursor: 'not-allowed'
    }
  };

  // Handle when advertiser details are fetched from /get_user_advertiser_details API
  useEffect(() => {
    if (advertisersForUserTimeZone && formData.email) {
      // DON'T auto-select organizations from advertiser data
      // Organizations should only be selected manually by the user
      // This prevents the auto-selection issue

      // Update advertiser access if we have data
      if (advertisersForUserTimeZone.utcAdvertisers || advertisersForUserTimeZone.jstAdvertisers) {
        const allAdvertisers = [...(advertisersForUserTimeZone.utcAdvertisers || []), ...(advertisersForUserTimeZone.jstAdvertisers || [])];

        if (allAdvertisers.length > 0) {
          const advertiserNames = allAdvertisers.map((adv) => adv.advertiser_name);

          if (advertiserNames.length > 0) {
            setFormData((prev) => ({
              ...prev,
              advertiserAccess: advertiserNames.join(', ')
            }));
          }
        }
      }
    }
  }, [advertisersForUserTimeZone, formData.email]);

  // Handle when current access data changes (including isAdmin checkbox)
  const handleCurrentAccessDataChange = (data) => {
    // Check if this is a remove_organization request
    if (data && data.type === 'remove_organization') {
      // Remove the organization from invitedUserAdvertiserDetails
      setInvitedUserAdvertiserDetails((prev) => {
        if (!prev) return prev;

        const newData = { ...prev };

        // Remove all advertisers from this organization
        if (newData.advertisers_utc) {
          newData.advertisers_utc = newData.advertisers_utc.filter((adv) => adv.org_name !== data.organization);
        }
        if (newData.advertisers_non_utc) {
          newData.advertisers_non_utc = newData.advertisers_non_utc.filter((adv) => adv.org_name !== data.organization);
        }

        // Also remove from org_utc if it exists
        if (newData.org_utc) {
          newData.org_utc = newData.org_utc.filter((org) => org !== data.organization);
        }

        return newData;
      });

      // Mark form as modified
      setIsFormModified(true);
      return;
    }

    // Update invitedUserAdvertiserDetails
    setInvitedUserAdvertiserDetails(data);

    // If isAdmin is included in the data, update the form state
    if ('isAdmin' in data) {
      setFormData((prev) => ({
        ...prev,
        isAdmin: data.isAdmin
      }));
    }

    // Always mark form as modified when current access data changes
    // This includes adding/removing advertisers, changing isAdmin, etc.
    setIsFormModified(true);
  };

  const isUserActuallyFound = formData.email && allUsersList.length > 0 && allUsersList.some((user) => user.email_id === formData.email);

  const isEmailValidButUserNotFound =
    formData.email && formData.email.includes('@inmobi.com') && !errors.email && !isUserActuallyFound && allUsersList.length > 0;

  const handleEmailBlur = () => {
    if (formData.email.trim() !== '') {
      // First, try to prefill from existing users list
      prefillFormFields(formData.email);

      // Then, fetch specific user advertiser details from the API
      // This ensures we get the most up-to-date information
      if (formData.email.includes('@inmobi.com')) {
        fetchAdvertisersListForUserBasedOnEmailId(formData.email);
      }
    }
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    console.log('🔍 Checkbox change:', {
      name,
      type,
      checked,
      fieldValue,
      currentFormData: formData
    });

    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: fieldValue
      };
      console.log('🔍 New form data after change:', newData);
      return newData;
    });

    // Mark form as modified for any change
    setIsFormModified(true);

    if (name !== 'email' && type !== 'checkbox') {
      validateField(name, fieldValue);
    }
  };

  const handleIAMInvite = (userData) => {
    const combinedName = `${userData.firstName} ${userData.lastName}`;
    sendIAMInvite({
      name: combinedName,
      email: userData.email,
      firstName: userData.firstName,
      lastName: userData.lastName,
      org: user_role === 'admin' ? formData.org : '',
      advertiserAccess: formData.advertiserAccess,
      isAdmin: formData.isAdmin
    });
  };

  const handleReset = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      org: '',
      advertiserAccess: '',
      isAdmin: false,
      newAdvertiser: ''
    });
    setSelectedOrgs([]);
    setInvitedUserAccess('');
    setIsFormModified(false);
  };

  const handleSubmit = () => {
    if (isFormValid()) {
      const combinedName = `${formData.firstName} ${formData.lastName}`;
      sendIAMInvite({
        name: combinedName,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        org: user_role === 'admin' ? formData.org : '',
        advertiserAccess: formData.advertiserAccess,
        isAdmin: formData.isAdmin
      });
    }
  };

  const handleClose = () => {
    if (closeModal) {
      closeModal(false);
    }
    if (resetIAMFormData) {
      resetIAMFormData();
    }
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      org: '',
      advertiserAccess: '',
      isAdmin: false,
      newAdvertiser: ''
    });
    setSelectedOrgs([]);
    setMovedFromExisting([]);
    setIsFormModified(false);
  };

  const handleApplyChanges = async () => {
    try {
      // Get current advertiser data from AdminAdvertiserAccess component (includes dynamic additions)
      const invitedUserData = allUsersList.find((u) => u.email_id === formData.email);

      // Get current advertiser data from the child component (includes dynamically added advertisers)
      const currentAdvertiserData = adminAdvertiserAccessRef.current?.getCurrentAdvertiserData?.() || {
        advertisers_utc: invitedUserData?.advertisers_utc || [],
        advertisers_non_utc: invitedUserData?.advertisers_non_utc || [],
        advertisers_ctv: invitedUserData?.advertisers_ctv || []
      };

      let orgNames = [];

      // Start with existing organizations from get_all_users_list
      const existingOrgs = invitedUserData?.org_utc || [];

      // Filter out removed organizations
      const filteredExistingOrgs = existingOrgs.filter((org) => !removedOrgs.includes(org));

      // Add filtered existing organizations
      orgNames.push(...filteredExistingOrgs);

      // Add newly selected organizations
      if (selectedOrgs && selectedOrgs.length > 0) {
        selectedOrgs.forEach((org) => {
          if (typeof org === 'string' && !orgNames.includes(org)) {
            orgNames.push(org);
          } else if (typeof org === 'object' && org.org_name && !orgNames.includes(org.org_name)) {
            orgNames.push(org.org_name);
          }
        });
      }

      const advertiserNames = formData.advertiserAccess ? formData.advertiserAccess.split(', ').filter((name) => name.trim() !== '') : [];
      const currentUser = allUsersList.find((u) => u.email_id === userEmail);
      const advertisers = [];

      // Use current advertiser data (includes dynamically added advertisers)
      const existingUtcAdvertisers = currentAdvertiserData?.advertisers_utc || [];

      existingUtcAdvertisers.forEach((adv) => {
        advertisers.push({
          name: adv.name,
          id: adv.id || adv.advertiser_guid || '',
          sfId: adv.sfId || adv.salesforce_account_id || adv.sf_id || '',
          timezone: 'UTC',
          platform: 'DSP'
        });
      });

      const existingJstAdvertisers = currentAdvertiserData?.advertisers_non_utc || [];

      existingJstAdvertisers.forEach((adv) => {
        advertisers.push({
          name: adv.name,
          id: adv.id || adv.advertiser_guid || '',
          sfId: adv.sfId || adv.salesforce_account_id || adv.sf_id || '',
          timezone: 'JST',
          platform: 'DSP'
        });
      });

      const existingCtvAdvertisers = currentAdvertiserData?.advertisers_ctv || [];
      existingCtvAdvertisers.forEach((adv) => {
        advertisers.push({
          name: adv.name,
          id: adv.id || adv.advertiser_guid || '',
          sfId: adv.sfId || adv.salesforce_account_id || adv.sf_id || '',
          timezone: 'UTC',
          platform: 'CTV'
        });
      });

      const existingGtvAdvertisers = currentAdvertiserData?.advertisers_gtv || [];
      existingGtvAdvertisers.forEach((adv) => {
        advertisers.push({
          name: adv.name,
          id: adv.id || adv.advertiser_guid || '',
          sfId: adv.sfId || adv.salesforce_account_id || adv.sf_id || '',
          timezone: 'UTC',
          platform: 'GTV'
        });
      });

      advertiserNames.forEach((advertiserName) => {
        const alreadyExists = advertisers.some((adv) => adv.name === advertiserName);
        if (!alreadyExists) {
          let timezone = 'UTC';
          const utcAdvertiser = existingUtcAdvertisers.find((adv) => adv.name === advertiserName);
          const jstAdvertiser = existingJstAdvertisers.find((adv) => adv.name === advertiserName);

          if (jstAdvertiser) {
            timezone = 'JST';
          }

          const advertiserId =
            jstAdvertiser?.id || utcAdvertiser?.id || jstAdvertiser?.advertiser_guid || utcAdvertiser?.advertiser_guid || '';
          const advertiserSfId =
            jstAdvertiser?.sfId ||
            utcAdvertiser?.sfId ||
            jstAdvertiser?.salesforce_account_id ||
            utcAdvertiser?.salesforce_account_id ||
            '';

          advertisers.push({
            name: advertiserName,
            id: advertiserId,
            sfId: advertiserSfId,
            timezone: timezone
          });
        }
      });

      let role = 'internal';

      // Get current user role from currentUserData (more reliable than user_role prop)
      const currentUserRole = currentUserData?.role || user_role;

      if (currentUserRole === 'admin') {
        role = formData.isAdmin ? 'admin' : 'internal';
      } else {
        role = invitedUserData?.role || 'internal';
      }

      const payload = {
        email_id: formData.email,
        org_utc: orgNames,
        role: role,
        advertisers: advertisers
      };

      const response = await fetch('/update_user_advertiser_settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        setSuccessMessage('Changes applied successfully! 🎉');
        setShowSuccessNotification(true);
        setIsFormModified(false);
      } else {
        const errorData = await response.json();
        setSuccessMessage(`Failed to apply changes: ${errorData.message || 'Unknown error'}`);
        setShowSuccessNotification(true);
      }
    } catch (error) {
      setSuccessMessage(`Error applying changes: ${error.message}`);
      setShowSuccessNotification(true);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        py: 3
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '1200px',
          mx: 'auto',
          px: { xs: 2, sm: 3, md: 4 }
        }}
      >
        {/* Organization Selector - REMOVED - Now handled by AdminAdvertiserAccess */}

        {/* Advertiser Access - Different for admin vs internal users */}
        {/* TEMPORARILY FORCE AdminAdvertiserAccess for testing */}
        {/* Force AdminAdvertiserAccess to show regardless of user role */}
        <AdminAdvertiserAccess
          ref={adminAdvertiserAccessRef}
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          allUsersList={allUsersList}
          selectedOrgs={selectedOrgs}
          setSelectedOrgs={setSelectedOrgs}
          removedOrgs={removedOrgs}
          advertiserMetaDetails={advertiserMetaDetails}
          currentUserData={currentUserData}
          fetchAdvertisersListForUserBasedOnEmailId={fetchAdvertisersListForUserBasedOnEmailId}
          fetchAdvertiserMetaDetails={fetchAdvertiserMetaDetails}
          onCurrentAccessDataChange={handleCurrentAccessDataChange}
          handleChange={handleChange}
          setIsFormModified={setIsFormModified}
          onRemovedOrgsChange={handleRemovedOrgsChange}
        />

        {/* Action Buttons - Apply, Reset, IAM Invite */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '24px',
            marginTop: '24px'
          }}
        >
          {/* Cancel Button */}
          <Button
            onClick={() => {
              openAdvertiserList();
            }}
            variant="outlined"
            color="inherit"
            size="large"
            sx={buttonStyles}
          >
            Cancel
          </Button>
          {/* Apply Changes Button */}
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleApplyChanges}
            disabled={!formData.email || !isFormModified}
            sx={buttonStyles}
          >
            Apply Changes
          </Button>

          {/* Reset Button */}
          <Button variant="outlined" color="inherit" size="large" onClick={handleReset} sx={buttonStyles}>
            Reset
          </Button>

          {/* IAM Invite Button */}
          <Button
            variant="contained"
            color="success"
            size="large"
            onClick={() => setShowIAMModal(true)}
            disabled={!formData.email || !formData.email.includes('@inmobi.com')}
            sx={buttonStyles}
          >
            IAM Invite
          </Button>
        </div>

        {/* Success Notification */}
        <Snackbar
          open={showSuccessNotification}
          autoHideDuration={4000}
          onClose={() => setShowSuccessNotification(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setShowSuccessNotification(false)}
            severity={
              successMessage.includes('successfully') ? 'success' : successMessage.includes('Outside your scope') ? 'warning' : 'error'
            }
            icon={successMessage.includes('successfully') ? <ThumbUpIcon /> : undefined}
            sx={{
              width: '100%',
              fontSize: '1rem',
              fontWeight: 500,
              '& .MuiAlert-icon': {
                fontSize: '1.5rem'
              }
            }}
          >
            {successMessage}
          </Alert>
        </Snackbar>

        {/* IAM Invite Modal */}
        <IAMInviteModal open={showIAMModal} onClose={() => setShowIAMModal(false)} email={formData.email} onSendInvite={handleIAMInvite} />
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => {
  return {
    onboardedIAMStatus: state.userReducer.onboardedIAM || false,
    iamUserList: state.userReducer.iamUserList || [],
    error: state.userReducer.error || null,
    userEmail: state.userReducer.userEmail || '',
    advertiserMetaDetails: state.userReducer.advertiserMetaDetails || {
      advertisers_utc: [],
      advertisers_non_utc: [],
      advertisers_ctv: []
    },
    advertisersForUserTimeZone: state.userReducer.advertisersForUserTimeZone || {
      utcAdvertisers: [],
      jstAdvertisers: [],
      orgNames: []
    }
  };
};

const mapDispatchToProps = {
  sendIAMInvite,
  resetIAMFormData,
  fetchAdvertisersListForUserBasedOnEmailId,
  fetchAdvertiserMetaDetails
};

AddInternalUser.propTypes = {
  sendIAMInvite: PropTypes.func.isRequired,
  onboardedIAMStatus: PropTypes.bool,
  resetIAMFormData: PropTypes.func.isRequired,
  fetchAdvertisersListForUserBasedOnEmailId: PropTypes.func.isRequired,
  fetchAdvertiserMetaDetails: PropTypes.func.isRequired,
  iamUserList: PropTypes.array,
  showModal: PropTypes.bool,
  closeModal: PropTypes.func,
  error: PropTypes.object,
  user_role: PropTypes.string,
  userEmail: PropTypes.string,
  advertiserMetaDetails: PropTypes.object,
  advertisersForUserTimeZone: PropTypes.object
};

AddInternalUser.defaultProps = {
  onboardedIAMStatus: false,
  iamUserList: [],
  showModal: false,
  closeModal: () => {},
  error: {},
  user_role: '',
  userEmail: ''
};

export default connect(mapStateToProps, mapDispatchToProps)(AddInternalUser);
